import React, { useState, ChangeEvent } from "react";
import { Modal } from "@mui/material";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { SERVER_URL } from "../../config";
import { toast } from "react-toastify";
import "../../styles/modals.css";

/* ── minimal props we actually need ───────────────────────────────────── */
export interface PrescriptionDetails {
  prescriptionId: string;
  medicationName: string;
  dosage: string;
  refills: number;
  quantity: number;
  instructions: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  prescription: PrescriptionDetails;
  patientName: string;
  providerName: string;
  providerUserId: string;
  patientProfileId: string;
  clinicId?: string;
}

const PrescriptionDeniedModal: React.FC<Props> = ({
  isOpen,
  onClose,
  prescription,
  patientName,
  providerName,
  providerUserId,
  patientProfileId,
  clinicId,
}) => {
  const [reason, setReason] = useState("");

  /* save denial + provider note */
  const save = async () => {
    try {
      await fetchWithAuth(
        `${SERVER_URL}/api/prescription/${prescription.prescriptionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            status: "DENIED",
            moreInfo: `${reason} — denied by ${providerName}`,
            providerUserId,
            clinicId,
          }),
        }
      );

      await fetchWithAuth(`${SERVER_URL}/api/providerNote/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          patientProfileId,
          providerId: providerUserId,
          note: reason,
        }),
      });

      toast.success("Prescription denied & note saved.");
      onClose();
    } catch (e) {
      console.error("[deny]", e);
      toast.error("Failed to save denial.");
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="deny-modal">
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            &times;
          </button>

          <h2>Deny Prescription</h2>

          <p>
            <strong>Patient:</strong> {patientName}
          </p>
          <p>
            <strong>Provider:</strong> {providerName}
          </p>
          <p>
            <strong>Medication:</strong> {prescription.medicationName}
          </p>
          <p>
            <strong>Dosage:</strong> {prescription.dosage}
          </p>
          <p>
            <strong>Quantity:</strong> {prescription.quantity}
          </p>
          <p>
            <strong>Instructions:</strong> {prescription.instructions}
          </p>

          <textarea
            className="rx-input rx-textarea"
            placeholder="Reason for denial"
            rows={3}
            value={reason}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setReason(e.target.value)
            }
          />

          <button className="modal-button primary" onClick={save}>
            Save & Close
          </button>
          <button className="modal-button secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PrescriptionDeniedModal;
